"use client";
import React, {
  CSSProperties,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  CardPreviewModalImageStyle,
  CardPreviewSizing,
  ChangePrintingCardSizing,
  ResponsiveCardSizing,
} from "utils/styles";
import { CardSkeleton } from "modules/common/components/CardSkeleton";
import { cn, getCardImageUrl, getInternalCardId } from "utils/utils";
import {
  AutocompleteGameCard,
  CardPrintingOption,
  CommonPagedCard,
  GameCard,
} from "modules/cards/model/card";
import { clientLogger } from "modules/common/logger/client";
import Image, { ImageProps } from "next/image";
import { DeckCardData } from "modules/decks/model/deck";

import { CollectionCard } from "modules/collections/details/model/collection";
import { TradeCard } from "modules/swap-calculator/model/tradeCard";
import { TradeItem } from "modules/tradeLists/model/tradeList";
import { ProposalItem } from "modules/trade-proposals/model/tradeProposal";
import { BaseCardItem } from "modules/common/components/CardGridItem/types";
import { CardFoilOverlay } from "./card/CardFoilOverlay";

export type SizingOptions =
  | typeof CardPreviewSizing
  | typeof ChangePrintingCardSizing
  | typeof ResponsiveCardSizing
  | typeof CardPreviewModalImageStyle
  | CSSProperties;

interface Props
  extends Pick<ImageProps, "fetchPriority" | "priority" | "loading"> {
  card:
    | GameCard
    | AutocompleteGameCard
    | DeckCardData
    | TradeCard
    | CollectionCard
    | TradeItem
    | CommonPagedCard
    | CardPrintingOption
    | ProposalItem
    | BaseCardItem;
  foil?: boolean;
  isBack?: boolean;
  sizing?: SizingOptions;
}
const MAX_RETRIES = 3;

export const CardPreviewLoading: React.FC<Props> = ({
  card,
  foil = false,
  isBack = false,
  sizing = CardPreviewSizing,
  priority,
  loading,
  fetchPriority,
}) => {
  const [imageLoading, setImageLoading] = useState(!priority);
  const [retryCount, setRetryCount] = useState(0);
  const imageWrapperRef = useRef<HTMLDivElement>(null);
  const hasImage = useMemo(() => !!getInternalCardId(card), [card]);
  const containerClasses =
    "border-base-content/20 relative box-border overflow-hidden rounded-[4.75%/3.5%] border shadow-md";
  const src = useMemo(() => getCardImageUrl(card, isBack), [card, isBack]);

  // Result-set view transitions can commit an image after the browser has
  // completed it but without React observing the load event. Reconcile from
  // the DOM immediately and keep a native listener attached so neither side
  // of that race can leave a loaded card hidden behind its skeleton.
  useLayoutEffect(() => {
    const img = imageWrapperRef.current?.querySelector("img");
    if (!img) return;

    const syncImageState = () => {
      setImageLoading(!(img.complete && img.naturalWidth > 0));
    };

    img.addEventListener("load", syncImageState);
    syncImageState();

    return () => img.removeEventListener("load", syncImageState);
  }, [src, retryCount]);

  const handleImageLoad = () => setImageLoading(false);
  const handleImageError = () => {
    if (retryCount < MAX_RETRIES) {
      // Exponential backoff: 1s, 2s, 4s
      const delay = Math.pow(2, retryCount) * 1000;
      setTimeout(() => {
        setRetryCount((prev) => prev + 1);
      }, delay);
    } else {
      clientLogger.error(
        "cardPreview.loadImage",
        "failed to load image after retries",
        new Error(`exhausted ${MAX_RETRIES} retries`),
        { cardName: card.name, retries: MAX_RETRIES }
      );
      setImageLoading(false);
    }
  };

  if (!hasImage && !src) {
    return (
      <div className={cn(containerClasses, "bg-base-300")} style={sizing}>
        <div className="absolute inset-0">
          <CardSkeleton
            costPrinted={"costPrinted" in card ? card.costPrinted : undefined}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={cn(containerClasses, "bg-base-300")} style={sizing}>
      {imageLoading && (
        <div className="absolute inset-0">
          <CardSkeleton
            costPrinted={"costPrinted" in card ? card.costPrinted : undefined}
          />
        </div>
      )}
      {src && (
        <div
          ref={imageWrapperRef}
          className={cn(
            "absolute inset-0 transition-opacity duration-200",
            imageLoading && "opacity-0"
          )}
        >
          {foil && !imageLoading && <CardFoilOverlay />}
          <Image
            key={`${src}-${retryCount}`}
            priority={false}
            loading={loading ?? (priority ? "eager" : "lazy")}
            fetchPriority={fetchPriority ?? (priority ? "high" : "auto")}
            width={240}
            height={336}
            quality={100}
            src={src}
            alt={`${card.name}${isBack ? " (back)" : ""}`}
            style={{
              ...CardPreviewModalImageStyle,
              width: "100%",
              height: "100%",
            }}
            onLoad={handleImageLoad}
            onError={handleImageError}
            sizes="(max-width: 768px) 146px, 488px"
          />
        </div>
      )}
    </div>
  );
};
